import { LIST_SEARCH_PARAM_FILTER_PREFIX } from "@constants/searchParams"

import {
    AppliedFilter,
    AppliedFilterInput,
    FilterGroup,
    FilterGroupOperators,
    FilterOperator,
    FilterProperty,
    FilterPropertyRegistry,
} from "./ObjectsView.types"

// The wire format the backend's filter groups read: `<property>__<operator>=<value>,<value>`.
const FILTER_PARAMETER_SEPARATOR = "__"
const FILTER_VALUE_SEPARATOR = ","

/** What each filter group offers: its operators, in the order the menus list them, and the one a new filter starts on. */
export const filterGroups: {
    [Group in FilterGroup]: {
        operators: readonly FilterGroupOperators[Group][]
        defaultOperator: FilterGroupOperators[Group]
    }
} = {
    choice: { operators: ["in", "not_in"], defaultOperator: "in" },
    set: { operators: ["all", "any", "not_all", "not_any"], defaultOperator: "any" },
    date: { operators: ["after", "before", "on", "within", "isnull"], defaultOperator: "after" },
    dateWindow: { operators: ["after", "before", "isnull"], defaultOperator: "after" },
    scalar: { operators: ["over", "under", "is", "within"], defaultOperator: "over" },
    address: { operators: ["contains", "not_contains"], defaultOperator: "contains" },
}

/** Return the operators a property offers, in the order the menus list them. */
export function getFilterPropertyOperators(property: FilterProperty): readonly FilterOperator[] {
    const operators: readonly FilterOperator[] = filterGroups[property.group].operators

    // Which timestamps fall on a day depends on the user's timezone, so a timestamp takes a day through `within`.
    return property.group === "date" && property.precision === "datetime"
        ? operators.filter((operator) => operator !== "on")
        : operators
}

/**
 * Return the request parameters of the applied filters, or `null` when none carries a value.
 *
 * Each filter is one `<property>__<operator>` parameter whose values comma-join (the backend ORs them), each
 * value percent-encoded so a comma inside it never splits it. Two filters on one property stay two parameters
 * with the same name, which the backend ANDs. A filter without values is still being configured and sends nothing.
 */
export function appliedFiltersToSearchParams(filters: readonly AppliedFilter[]): URLSearchParams | null {
    const searchParams = new URLSearchParams()

    filters.forEach((filter) => {
        if (filter.values.length > 0) {
            searchParams.append(
                `${filter.property}${FILTER_PARAMETER_SEPARATOR}${filter.operator}`,
                filter.values.map((value) => encodeURIComponent(value)).join(FILTER_VALUE_SEPARATOR),
            )
        }
    })

    return searchParams.toString() === "" ? null : searchParams
}

/**
 * Read a URL's `filter__<property>__<operator>` parameters for the registered properties.
 *
 * Each repetition of a parameter is one filter, the way the backend reads it. A parameter for an unregistered
 * property or an operator the property does not offer is left alone; the names of the ones read come back so the
 * caller can strip them.
 */
export function parseFilterURLEntries(search: string, filterProperties: FilterPropertyRegistry) {
    const searchParams = new URLSearchParams(search)
    const filters: AppliedFilterInput[] = []
    const consumedParameterNames = new Set<string>()

    filterProperties.forEach((property) => {
        getFilterPropertyOperators(property).forEach((operator) => {
            const parameterName = `${LIST_SEARCH_PARAM_FILTER_PREFIX}${property.key}${FILTER_PARAMETER_SEPARATOR}${operator}`

            searchParams.getAll(parameterName).forEach((rawValue) => {
                try {
                    const values = rawValue
                        .split(FILTER_VALUE_SEPARATOR)
                        .filter((value) => value !== "")
                        .map((value) => decodeURIComponent(value))

                    if (values.length > 0) {
                        filters.push({ property: property.key, operator, values })
                        consumedParameterNames.add(parameterName)
                    }
                } catch {
                    // A hand-edited link can carry a malformed escape; that repetition is skipped.
                }
            })
        })
    })

    return { filters, consumedParameterNames: [...consumedParameterNames] }
}
